// File: src/types/techLog/trip.zod.ts
import { Zdate, ZdateTime } from '../common.zod';
import { ZTechLogAttachment } from './attachment.zod';
import { z } from 'zod';

// Custom data schema - flexible to handle various structures or empty objects
const ZcustomData = z
  .record(z.unknown())
  .describe('Custom data associated with the trip');

// The fixed set TechLog accepts on a trip write (techlog.apidocs.yml
// TripUpdateBodyV2 / TripCreateBodyV2). A value outside it is answered with
// `400 {"errors":["tripCategory does not have a valid value"]}`, so source
// systems with their own category vocabulary must map onto these five.
export const ZTechlogTripCategory = z
  .enum(['private', 'commercial', 'scheduled', 'maintenance', 'training'])
  .describe('Category of the trip');

// TechLogTrip schema - reflects actual TechLog API response with nullable fields
export const ZTechLogTrip = z.object({
  id: z.string().describe('Unique identifier for the trip (TechlogTripId)'),
  aircraftId: z.string().describe('ID of the aircraft used for this trip'),
  pilotInCommandId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of the Pilot in Command (PIC) for this trip'),
  secondInCommandId: z
    .string()
    .nullable()
    .optional()
    .describe(
      'ID of the Second in Command (SIC) or First Officer for this trip',
    ),
  date: Zdate.describe('Date when the trip took place'),
  number: z
    .number()
    .nullable()
    .optional()
    .describe('Trip number as assigned by the operator'),
  srpNumber: z
    .number()
    .nullable()
    .optional()
    .describe('Sector Reference Point number for this trip'),
  callsign: z
    .string()
    .nullable()
    .optional()
    .describe('Radio callsign used for this trip (e.g., "N123AB")'),
  attachments: z
    .array(ZTechLogAttachment)
    .optional()
    .describe('Documents or files attached to this trip'),
  createdAt: ZdateTime.describe('Timestamp when this trip record was created'),
  updatedAt: ZdateTime.describe(
    'Timestamp when this trip record was last updated',
  ),
  isComplete: z
    .boolean()
    .nullable()
    .optional()
    .describe('Whether the trip has been completed'),
  signatureId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of the signature for trip initiation'),
  completionSignatureId: z
    .string()
    .nullable()
    .optional()
    .describe('ID of the signature for trip completion'),
  customData: ZcustomData.optional().describe(
    'Additional custom data for this trip',
  ),
  justificationDescription: z
    .string()
    .nullable()
    .optional()
    .describe('Description of any special justifications for this trip'),
  tripCategory: z
    .string()
    .nullable()
    .optional()
    .describe('Category of the trip (e.g., "commercial" or "private")'),
  abandonmentReason: z
    .string()
    .nullable()
    .optional()
    .describe('Reason for trip abandonment if applicable'),
  thirdPartyReferences: z
    .record(z.unknown())
    .optional()
    .describe('References to third-party systems'),
});

// TechlogTripId type
export const ZTechlogTripId = z
  .string()
  .describe('Unique identifier for a techlog trip');

// TechlogTripCreateInput schema
export const ZTechlogTripCreateInput = z.object({
  id: ZTechlogTripId.optional().describe(
    'Unique identifier for the trip (used for sync storage)',
  ),
  aircraftId: z
    .string()
    .optional()
    .describe('ID of the aircraft to be used for this trip'),
  pilotInCommandId: z
    .string()
    .optional()
    .describe('ID of the Pilot in Command (PIC) for this trip'),
  secondInCommandId: z
    .string()
    .optional()
    .describe(
      'ID of the Second in Command (SIC) or First Officer for this trip',
    ),
  date: z.string().optional().describe('Date when the trip will take place'),
  number: z
    .string()
    .optional()
    .describe('Trip number as assigned by the operator'),
  srpNumber: z
    .number()
    .optional()
    .describe('Sector Reference Point number for this trip'),
  callsign: z
    .string()
    .optional()
    .describe('Radio callsign to be used for this trip (e.g., "N123AB")'),
  tripCategory: ZTechlogTripCategory.optional(),
  eta: z.string().optional().describe('Estimated Time of Arrival'),
  etd: z.string().optional().describe('Estimated Time of Departure'),
  flights: z
    .array(z.any())
    .optional()
    .describe('List of flights associated with this trip'),
});

export const ZTechLogTripUpdateUtilisation = z.object({
  airframe_seconds: z.number().optional(),
  airframe_cycles: z.number().optional(),
  airframe_landings: z.number().optional(),
  airframe_flights: z.number().optional(),
  engine_1_cycles: z.number().optional(),
  engine_2_cycles: z.number().optional(),
  engine_1_seconds: z.number().optional(),
  engine_2_seconds: z.number().optional(),
  apu_seconds: z.number().optional(),
  apu_cycles: z.number().optional(),
});

export const ZTechLogTripUpdate = z.object({
  id: z.string(),
  aircraftId: z.string(),
  date: Zdate,
  number: z.number().optional(),
  srpNumber: z.number().optional(),
  customData: z.record(z.unknown()).optional(),
  utilisation: ZTechLogTripUpdateUtilisation.optional(),
  carriedForwardsOverride: z.record(z.number()).optional(),
  broughtForwardsOverride: z.record(z.number()).optional(),
  createdAt: ZdateTime,
  updatedAt: ZdateTime,
});
